import {Anchor} from '@mantine/core';
import {NavLink} from 'react-router-dom';
import {DataTableColumn} from 'mantine-datatable';
import {I18n} from '@aws-amplify/core';

import {ReconciliationReportRow} from '../types.ts';
import {ChannelBadge, DiscrepancyBadge} from '../profile/Badges.tsx';
import {formatTimestamp, TIMESTAMP_PLACEHOLDER} from '../../../utils/TableTimestamps.tsx';
import {detailPath} from '../config';

// Every column is sortable (§3a). The comparator is the shared sortRecords, applied in
// useReconciliationReports; these accessors are what it sorts BY, so each one is a real
// field on the row. Status sorts on discrepancyTotal, derived in the hook from the same
// three counts the badge reads.
export const useReconciliationReportsTableColumns = () => {

    const columns: DataTableColumn<ReconciliationReportRow>[] = [
        {
            accessor: 'channel',
            title: I18n.get('Channel'),
            sortable: true,
            render: (item) => <ChannelBadge channel={item.channel}/>,
        },
        {
            accessor: 'runDate',
            title: I18n.get('Run Date'),
            sortable: true,
            noWrap: true,
            // A real link, so the detail page is reachable from the keyboard as well as
            // by row click. stopPropagation keeps the row's own click from navigating twice.
            render: (item) => (
                <Anchor component={NavLink} to={detailPath(item.id)} onClick={(e) => e.stopPropagation()}>
                    {item.runDate ?? TIMESTAMP_PLACEHOLDER}
                </Anchor>
            ),
        },
        {
            accessor: 'runStartedAt',
            title: I18n.get('Run Started'),
            sortable: true,
            render: (item) => formatTimestamp(item.runStartedAt),
        },
        {
            accessor: 'runCompletedAt',
            title: I18n.get('Run Completed'),
            sortable: true,
            render: (item) => formatTimestamp(item.runCompletedAt),
        },
        {
            accessor: 'oursButNotTheirsCount',
            title: I18n.get('Ours Only'),
            sortable: true,
            render: (item) => item.oursButNotTheirsCount ?? 0,
        },
        {
            accessor: 'theirsButNotOursCount',
            title: I18n.get('Theirs Only'),
            sortable: true,
            render: (item) => item.theirsButNotOursCount ?? 0,
        },
        {
            accessor: 'quantityMismatchCount',
            title: I18n.get('Qty Mismatch'),
            sortable: true,
            render: (item) => item.quantityMismatchCount ?? 0,
        },
        {
            accessor: 'discrepancyTotal',
            title: I18n.get('Status'),
            sortable: true,
            // The badge carries the count ("15 discrepancies"). Mantine's badge label
            // clips with an ellipsis, so the cell needs room for the longest label;
            // at 1440px an auto-width cell cut it to "15 DISCRE…".
            noWrap: true,
            width: 170,
            render: (item) => <DiscrepancyBadge record={item}/>,
        },
    ];

    return {
        columns,
    }
}
